import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useResetPassword } from '../auth/session';
import { AuthLayout } from './AuthLayout';
import { Banner, Button, Field, TextInput } from '../components/ui';
import { fieldErrors, errorMessage } from '../api/problems';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const userId = params.get('userId') ?? '';
  const token = params.get('token') ?? '';

  const reset = useResetPassword();
  const [newPassword, setNewPassword] = useState('');
  const errors = fieldErrors(reset.error);

  if (!userId || !token) {
    return (
      <AuthLayout title="Broken link">
        <Banner tone="error">
          That reset link is missing information. Request a new one.
        </Banner>
        <Link to="/forgot-password" className="text-sm text-accent hover:underline">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthLayout title="Password changed">
        <Banner tone="success">
          Your password is updated and every other session has been signed out.
        </Banner>
        <Link to="/login" className="text-sm text-accent hover:underline">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          reset.mutate({ userId, token, newPassword });
        }}
      >
        {reset.isError && !errors['NewPassword'] && (
          <Banner tone="error">
            {errorMessage(reset.error, 'That reset link is invalid or has expired.')}
          </Banner>
        )}

        <Field
          label="New password"
          hint="At least 12 characters."
          error={errors['NewPassword']?.[0]}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              invalid={Boolean(errors['NewPassword'])}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" disabled={reset.isPending}>
          {reset.isPending ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthLayout>
  );
}
